import { AccordionToggle } from "@root/shared/components/AccordionToggle";
import { Checkbox } from "@root/shared/components/Checkbox";
import { type Choice, ChoiceChip } from "@root/shared/components/ChoiceChip";
import { useState } from "react";

type ControlledProps<T> = {
  values: T[];
  defaultValues?: never;
  onChange: (values: T[]) => void;
};

type UncontrolledProps<T> = {
  values?: never;
  defaultValues: T[];
  onChange?: (values: T[]) => void;
};

export type GroupedChoiceChipProps<T> = (ControlledProps<T> | UncontrolledProps<T>) & {
  title: string;
  id: string;
  name?: string;
  groupValue: T;
  choices: Choice<T>[];
  defaultIsOpened?: boolean;
};

export const GroupedChoiceChip = <T extends string | number>(props: GroupedChoiceChipProps<T>) => {
  const { title, id, name, groupValue, values, onChange, defaultValues, choices, defaultIsOpened } =
    props;

  const [internalValues, setInternalValues] = useState<T[]>(defaultValues ?? []);

  const isControlled = values !== undefined;
  const currentValues = isControlled ? values : internalValues;

  const handleGroupChange = (checked: boolean) => {
    const newValues = checked ? [groupValue] : [];
    onChange?.(newValues);
    if (!isControlled) setInternalValues(newValues);
  };

  const handleChoiceChange = (newValues: T[]) => {
    onChange?.(newValues);
    if (!isControlled) setInternalValues(newValues);
  };

  return (
    <AccordionToggle
      title={
        <Checkbox
          id={id}
          label={title}
          checked={currentValues.length > 0}
          onChange={(e) => handleGroupChange(e.target.checked)}
          onLabelClick={(e) => e.stopPropagation()}
        />
      }
      defaultIsOpened={defaultIsOpened}
      ariaLabelPrefix={title}
    >
      <ChoiceChip
        name={name}
        values={
          currentValues.includes(groupValue) ? choices.map((choice) => choice.value) : currentValues
        }
        choices={choices}
        onChange={(value) => handleChoiceChange(value)}
      />
    </AccordionToggle>
  );
};
